/* eslint-disable react/prop-types */
"use client";

import { useEffect, useMemo, useState } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  AreaChart,
  Area,
} from "recharts";
import { ChevronDown } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import Layout2 from "@/components/Layout/Layout2";
import LeftBar2 from "@/components/Layout/LeftBar2";
import { dashboardLinks } from "@/components/lib/SidebarLinks";
import DashboardRangeTabs from "@/components/dashboard/dashboard-range-tabs";
import { Skeleton } from "@/components/ui/skeleton";
import useAuth from "@/hooks/useAuth";
import { formatCurrency } from "@/lib/utils";
import { fetchLoyaltyReports, fetchOfferReports } from "@/redux/slice/dashboard/dashboard-slice";

const COLORS = {
  giftCard: "#5850EC",
  c2c: "#A855F7",
  voucher: "#10B981",
  points: "#22D3EE",
  yellow: "#FBBF24",
  grid: "rgba(255, 255, 255, 0.05)",
};

const OFFER_COLORS = {
  buyGetFree: "#FBBF24",
  buyGetDiscount: "#EF4444",
  spendGetFree: "#10B981",
  spendGetDiscount: "#3B82F6",
  onlineDiscount: "#A855F7",
  yellow: "#FBBF24",
  grid: "rgba(255, 255, 255, 0.05)",
};

const YEAR_COLORS = ["#3B82F6", "#22D3EE", "#A855F7", "#10B981", "#FBBF24"];

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const CustomTooltip = ({ active, payload, label, currency = "GBP" }) => {
  if (active && payload && payload.length) {
    const value = Number(payload[0]?.value || 0);

    return (
      <div className="bg-[#1F2937] border border-white/10 px-4 py-2 rounded-md shadow-2xl flex flex-col items-center relative">
        <p className="text-white text-sm font-semibold">
          {formatCurrency(value, currency)}
        </p>
        <p className="text-gray-400 text-[10px]">{label}</p>
        <div className="absolute -bottom-1 w-2 h-2 bg-[#1F2937] rotate-45 border-r border-b border-white/10"></div>
      </div>
    );
  }
  return null;
};

const ChartSkeleton = ({ height }) => (
  <div className="w-full" style={{ height }}>
    <Skeleton className="h-full w-full" />
  </div>
);

const StatValue = ({ value, color, loading, currency }) => {
  if (loading) return <Skeleton className="h-8 w-24" />;

  return (
    <p className="text-2xl font-semibold" style={{ color }}>
      {formatCurrency(value || 0, currency)}
    </p>
  );
};

export default function LoyaltyReports() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const yearOptions = useMemo(
    () => [currentYear, currentYear - 1],
    [currentYear],
  );

  const recentMonths = useMemo(
    () =>
      [2, 1, 0].map((offset) => {
        const date = new Date(currentYear, currentMonth - 1 - offset, 1);
        return {
          value: date.getMonth() + 1,
          label: MONTH_NAMES[date.getMonth()],
        };
      }),
    [currentMonth, currentYear],
  );

  // Loyalty Filters
  const [loyaltyComparisonRange, setLoyaltyComparisonRange] = useState("weekly");
  const [loyaltyComparisonYear, setLoyaltyComparisonYear] = useState(currentYear);
  const [loyaltyComparisonMonth, setLoyaltyComparisonMonth] = useState(currentMonth);

  const [loyaltyTotalRange, setLoyaltyTotalRange] = useState("monthly");
  const [loyaltyTotalYear, setLoyaltyTotalYear] = useState(currentYear);
  const [loyaltyTotalMonth, setLoyaltyTotalMonth] = useState(currentMonth);

  const [loyaltyCompareYear1, setLoyaltyCompareYear1] = useState(currentYear - 1);
  const [loyaltyCompareYear2, setLoyaltyCompareYear2] = useState(currentYear);

  // Offer Filters
  const [offerComparisonRange, setOfferComparisonRange] = useState("weekly");
  const [offerComparisonYear, setOfferComparisonYear] = useState(currentYear);
  const [offerComparisonMonth, setOfferComparisonMonth] = useState(currentMonth);

  const [offerTotalRange, setOfferTotalRange] = useState("monthly");
  const [offerTotalYear, setOfferTotalYear] = useState(currentYear);
  const [offerTotalMonth, setOfferTotalMonth] = useState(currentMonth);

  const [offerCompareYear1, setOfferCompareYear1] = useState(currentYear - 1);
  const [offerCompareYear2, setOfferCompareYear2] = useState(currentYear);

  const loyaltyReports = useSelector((state) => state.dashboard?.loyaltyReports);
  const offerReports = useSelector((state) => state.dashboard?.offerReports);
  const loyaltyLoading = useSelector((state) => state.dashboard?.loyaltyLoading);
  const offerLoading = useSelector((state) => state.dashboard?.offerLoading);

  const restaurantId = user?.restaurant_id;
  const currency = user?.currency || "GBP";

  // Loyalty Charts Data
  const loyaltyComparisonData = useMemo(
    () => loyaltyReports?.charts?.comparison?.[loyaltyComparisonRange] || [],
    [loyaltyReports, loyaltyComparisonRange],
  );

  const loyaltyTotalValueData = useMemo(
    () => loyaltyReports?.charts?.totalValue?.[loyaltyTotalRange] || [],
    [loyaltyReports, loyaltyTotalRange],
  );

  const loyaltyYearlyComparisonData = useMemo(
    () => loyaltyReports?.charts?.yearlyComparison?.yearly || [],
    [loyaltyReports],
  );

  const loyaltyYearlyKeys = useMemo(() => {
    const firstRow = loyaltyYearlyComparisonData?.[0] || {};
    return Object.keys(firstRow)
      .filter((key) => key !== "label")
      .sort((a, b) => Number(a.replace("v", "")) - Number(b.replace("v", "")));
  }, [loyaltyYearlyComparisonData]);

  const loyaltyTotalValueFromFilteredData = useMemo(
    () =>
      loyaltyTotalValueData.reduce((acc, item) => acc + Number(item?.value || 0), 0),
    [loyaltyTotalValueData],
  );

  // Offer Charts Data
  const offerComparisonData = useMemo(
    () => offerReports?.charts?.comparison?.[offerComparisonRange] || [],
    [offerReports, offerComparisonRange],
  );

  const offerTotalValueData = useMemo(
    () => offerReports?.charts?.totalValue?.[offerTotalRange] || [],
    [offerReports, offerTotalRange],
  );

  const offerYearlyComparisonData = useMemo(
    () => offerReports?.charts?.yearlyComparison?.yearly || [],
    [offerReports],
  );

  const offerYearlyKeys = useMemo(() => {
    const firstRow = offerYearlyComparisonData?.[0] || {};
    return Object.keys(firstRow)
      .filter((key) => key !== "label")
      .sort((a, b) => Number(a.replace("v", "")) - Number(b.replace("v", "")));
  }, [offerYearlyComparisonData]);

  const offerTotalValueFromFilteredData = useMemo(
    () =>
      offerTotalValueData.reduce((acc, item) => acc + Number(item?.value || 0), 0),
    [offerTotalValueData],
  );

  const formatWeeklyAxisTick = (value) => {
    const numericValue = Number(value || 0);

    if (numericValue >= 1000) {
      const compactValue = numericValue / 1000;
      return `${Number.isInteger(compactValue) ? compactValue : compactValue.toFixed(1)}k`;
    }

    return `${numericValue}`;
  };

  // Loyalty Loadings
  const isLoyaltyInitialLoading = Boolean(loyaltyLoading?.initial);
  const isLoyaltyComparisonLoading =
    isLoyaltyInitialLoading || Boolean(loyaltyLoading?.comparison);
  const isLoyaltyTotalLoading =
    isLoyaltyInitialLoading || Boolean(loyaltyLoading?.totalValue);
  const isLoyaltyYearlyLoading =
    isLoyaltyInitialLoading || Boolean(loyaltyLoading?.yearlyComparison);

  // Offer Loadings
  const isOfferInitialLoading = Boolean(offerLoading?.initial);
  const isOfferComparisonLoading =
    isOfferInitialLoading || Boolean(offerLoading?.comparison);
  const isOfferTotalLoading =
    isOfferInitialLoading || Boolean(offerLoading?.totalValue);
  const isOfferYearlyLoading =
    isOfferInitialLoading || Boolean(offerLoading?.yearlyComparison);

  const loyaltyItems = [
    { key: "giftCard", label: "Gift Card", color: COLORS.giftCard },
    { key: "c2c", label: "C2C", color: COLORS.c2c },
    { key: "voucher", label: "Voucher", color: COLORS.voucher },
    { key: "points", label: "Points", color: COLORS.points },
  ];

  const offerItems = [
    { key: "buyGetFree", label: "Buy & Get Free", color: OFFER_COLORS.buyGetFree },
    { key: "buyGetDiscount", label: "Buy & Get Discount", color: OFFER_COLORS.buyGetDiscount },
    { key: "spendGetFree", label: "Spend Over & Get Free", color: OFFER_COLORS.spendGetFree },
    { key: "spendGetDiscount", label: "Spend & Get Discount", color: OFFER_COLORS.spendGetDiscount },
    { key: "onlineDiscount", label: "Online Order Discount", color: OFFER_COLORS.onlineDiscount },
  ];

  // Loyalty Requests
  const requestLoyaltyComparisonData = (
    period,
    year = loyaltyComparisonYear,
    month = loyaltyComparisonMonth,
  ) => {
    const params =
      period === "monthly"
        ? { period: "monthly", year, month }
        : { period: "weekly" };

    dispatch(
      fetchLoyaltyReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "comparison" },
      }),
    );
  };

  const requestLoyaltyTotalValueData = (
    period,
    year = loyaltyTotalYear,
    month = loyaltyTotalMonth,
  ) => {
    const params =
      period === "yearly"
        ? { period: "yearly", year }
        : { period: "monthly", year, month };

    dispatch(
      fetchLoyaltyReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "totalValue" },
      }),
    );
  };

  const requestLoyaltyYearlyComparisonData = (
    year1 = loyaltyCompareYear1,
    year2 = loyaltyCompareYear2,
  ) => {
    dispatch(
      fetchLoyaltyReports({
        restaurant_id: restaurantId,
        params: {
          period: "yearly",
          compare_year1: year1,
          compare_year2: year2,
        },
        requestMeta: { chart: "yearlyComparison" },
      }),
    );
  };

  // Offer Requests
  const requestOfferComparisonData = (
    period,
    year = offerComparisonYear,
    month = offerComparisonMonth,
  ) => {
    const params =
      period === "monthly"
        ? { period: "monthly", year, month }
        : { period: "weekly" };

    dispatch(
      fetchOfferReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "comparison" },
      }),
    );
  };

  const requestOfferTotalValueData = (
    period,
    year = offerTotalYear,
    month = offerTotalMonth,
  ) => {
    const params =
      period === "yearly"
        ? { period: "yearly", year }
        : { period: "monthly", year, month };

    dispatch(
      fetchOfferReports({
        restaurant_id: restaurantId,
        params,
        requestMeta: { chart: "totalValue" },
      }),
    );
  };

  const requestOfferYearlyComparisonData = (
    year1 = offerCompareYear1,
    year2 = offerCompareYear2,
  ) => {
    dispatch(
      fetchOfferReports({
        restaurant_id: restaurantId,
        params: {
          period: "yearly",
          compare_year1: year1,
          compare_year2: year2,
        },
        requestMeta: { chart: "yearlyComparison" },
      }),
    );
  };

  useEffect(() => {
    if (!restaurantId) return;

    dispatch(
      fetchLoyaltyReports({
        restaurant_id: restaurantId,
        params: { period: "weekly" },
        requestMeta: { mode: "initial" },
      }),
    );
    dispatch(
      fetchOfferReports({
        restaurant_id: restaurantId,
        params: { period: "weekly" },
        requestMeta: { mode: "initial" },
      }),
    );
  }, [dispatch, restaurantId]);

  return (
    <Layout2
      LeftBar={<LeftBar2 sidebarLinks={dashboardLinks} />}
      pageName={{ title: "Loyalty & Offer Reports" }}
      paths={[
        { name: "Dashboard", path: "/dashboard/overview" },
        { name: "Reports & Analytics", path: "/dashboard/revenue-reports" },
        { name: "Loyalty & Offer", path: "/dashboard/loyalty-reports" },
      ]}
    >
      <div className="container mx-auto p-6 space-y-16 min-h-screen text-white font-sans">
        
        {/* ========================================================================= */}
        {/* LOYALTY REPORT SECTION */}
        {/* ========================================================================= */}
        <div className="space-y-8">
          <div className="flex justify-between items-center border-b border-white/5 pb-4">
            <h1 className="text-2xl font-semibold tracking-wide text-white">Loyalty Report</h1>
            
            <div className="flex items-center gap-3">
              {loyaltyComparisonRange === "monthly" && (
                <>
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8 text-white"
                      value={loyaltyComparisonMonth}
                      onChange={(e) => {
                        const m = Number(e.target.value);
                        setLoyaltyComparisonMonth(m);
                        requestLoyaltyComparisonData("monthly", loyaltyComparisonYear, m);
                      }}
                    >
                      {recentMonths.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={14}
                      className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8 text-white"
                      value={loyaltyComparisonYear}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setLoyaltyComparisonYear(y);
                        requestLoyaltyComparisonData("monthly", y, loyaltyComparisonMonth);
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={14}
                      className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </>
              )}
              <DashboardRangeTabs
                options={[
                  { label: "Weekly", value: "weekly" },
                  { label: "Monthly", value: "monthly" },
                ]}
                value={loyaltyComparisonRange}
                onChange={(value) => {
                  setLoyaltyComparisonRange(value);
                  if (value === "weekly") {
                    requestLoyaltyComparisonData("weekly");
                  } else {
                    requestLoyaltyComparisonData("monthly", loyaltyComparisonYear, loyaltyComparisonMonth);
                  }
                }}
                shape="full"
                className="border-white/10"
                buttonClassName="px-4 md:px-5 py-1.5 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-4 gap-5">
            {loyaltyItems.map((item) => (
              <div
                key={item.key}
                className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 h-36 flex flex-col justify-between shadow-lg"
              >
                <p className="text-gray-400 text-sm font-medium">{item.label}</p>
                <StatValue
                  value={loyaltyReports?.stats?.[item.key] || 0}
                  color={item.color}
                  loading={isLoyaltyInitialLoading && !loyaltyReports}
                  currency={currency}
                />
              </div>
            ))}
          </div>

          <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8 relative">
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-lg font-medium">
                {loyaltyComparisonRange === "weekly" ? "Weekly" : "Monthly"} Comparison
              </h2>
              <div className="flex gap-6 text-[11px] font-medium tracking-wide">
                {loyaltyItems.map((item) => (
                  <span key={item.key} className="flex items-center gap-2 text-gray-300">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />{" "}
                    {item.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="h-[400px] w-full">
              {isLoyaltyComparisonLoading ? (
                <ChartSkeleton height={400} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  {loyaltyComparisonRange === "weekly" ? (
                    <BarChart
                      data={loyaltyComparisonData}
                      barCategoryGap="18%"
                      barGap={2}
                    >
                      <CartesianGrid
                        vertical={false}
                        stroke="#6B7280"
                        strokeOpacity={0.45}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#F3F4F6", fontSize: 12 }}
                        dy={10}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#F3F4F6", fontSize: 12 }}
                        tickFormatter={formatWeeklyAxisTick}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.03)" }}
                        content={<CustomTooltip currency={currency} />}
                      />
                      {loyaltyItems.map((item) => (
                        <Bar
                          key={item.key}
                          dataKey={item.key}
                          fill={item.color}
                          radius={[2, 2, 0, 0]}
                          barSize={14}
                        />
                      ))}
                    </BarChart>
                  ) : (
                    <AreaChart data={loyaltyComparisonData}>
                      <defs>
                        {loyaltyItems.map((item) => (
                          <linearGradient
                            key={item.key}
                            id={`grad-loyalty-${item.key}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor={item.color}
                              stopOpacity={0.15}
                            />
                            <stop
                              offset="95%"
                              stopColor={item.color}
                              stopOpacity={0}
                            />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid vertical={false} stroke={COLORS.grid} />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#666", fontSize: 12 }}
                        dy={15}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#666", fontSize: 12 }}
                      />
                      <Tooltip content={<CustomTooltip currency={currency} />} />
                      {loyaltyItems.map((item) => (
                        <Area
                          key={item.key}
                          type="monotone"
                          dataKey={item.key}
                          stroke={item.color}
                          strokeWidth={2.5}
                          fill={`url(#grad-loyalty-${item.key})`}
                          dot={false}
                        />
                      ))}
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              )}
            </div>
          </div>


            
            {/* Loyalty Total Value Chart */}
            <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8 flex flex-col justify-between">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-gray-400 text-sm mb-1 font-medium">Total Value</p>
                  {isLoyaltyTotalLoading ? (
                    <Skeleton className="h-10 w-36" />
                  ) : (
                    <p className="text-3xl font-bold text-[#FBBF24]">
                      {formatCurrency(loyaltyTotalValueFromFilteredData, currency)}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {loyaltyTotalRange === "monthly" && (
                    <div className="relative">
                      <select
                        className="bg-[#1A1A1A] rounded px-2 py-1 border border-white/10 text-xs appearance-none pr-6 text-white"
                        value={loyaltyTotalMonth}
                        onChange={(e) => {
                          const m = Number(e.target.value);
                          setLoyaltyTotalMonth(m);
                          requestLoyaltyTotalValueData("monthly", loyaltyTotalYear, m);
                        }}
                      >
                        {recentMonths.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={12}
                        className="text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] rounded px-3 py-1 border border-white/10 text-xs appearance-none pr-6 text-white"
                      value={loyaltyTotalYear}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setLoyaltyTotalYear(y);
                        if (loyaltyTotalRange === "yearly") {
                          requestLoyaltyTotalValueData("yearly", y);
                        } else {
                          requestLoyaltyTotalValueData("monthly", y, loyaltyTotalMonth);
                        }
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={12}
                      className="text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                  <DashboardRangeTabs
                    options={[
                      { label: "M", value: "monthly" },
                      { label: "Y", value: "yearly" },
                    ]}
                    value={loyaltyTotalRange}
                    onChange={(value) => {
                      setLoyaltyTotalRange(value);
                      if (value === "yearly") {
                        requestLoyaltyTotalValueData("yearly", loyaltyTotalYear);
                      } else {
                        requestLoyaltyTotalValueData("monthly", loyaltyTotalYear, loyaltyTotalMonth);
                      }
                    }}
                    shape="full"
                    className="border-white/10"
                    buttonClassName="px-2 py-1 text-[10px]"
                  />
                </div>
              </div>

              <div className="h-[400px] w-full">
                {isLoyaltyTotalLoading ? (
                  <ChartSkeleton height={300} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={loyaltyTotalValueData}>
                      <CartesianGrid
                        vertical={false}
                        stroke={COLORS.grid}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 11 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 10 }}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.02)" }}
                        content={<CustomTooltip currency={currency} />}
                      />
                      <Bar
                        dataKey="value"
                        fill={COLORS.points}
                        radius={[4, 4, 0, 0]}
                        barSize={16}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Loyalty Yearly Comparison Chart */}
            <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8 flex flex-col justify-between">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-sm font-medium text-gray-300">Yearly Comparison</h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] border border-white/10 rounded px-2 py-1 text-xs text-gray-300 appearance-none pr-6"
                      value={loyaltyCompareYear1}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setLoyaltyCompareYear1(y);
                        requestLoyaltyYearlyComparisonData(y, loyaltyCompareYear2);
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={`left-${year}`} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={12}
                      className="text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                  <span className="text-[10px] text-gray-600">Vs</span>
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] border border-white/10 rounded px-2 py-1 text-xs text-gray-300 appearance-none pr-6"
                      value={loyaltyCompareYear2}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setLoyaltyCompareYear2(y);
                        requestLoyaltyYearlyComparisonData(loyaltyCompareYear1, y);
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={`right-${year}`} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={12}
                      className="text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </div>
              </div>

              <div className="h-[400px] w-full">
                {isLoyaltyYearlyLoading ? (
                  <ChartSkeleton height={300} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={loyaltyYearlyComparisonData}>
                      <CartesianGrid
                        vertical={true}
                        horizontal={false}
                        stroke={COLORS.grid}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#666", fontSize: 11 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#666", fontSize: 10 }}
                      />
                      <Tooltip content={<CustomTooltip currency={currency} />} />
                      {loyaltyYearlyKeys.map((key, index) => (
                        <Area
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={YEAR_COLORS[index % YEAR_COLORS.length]}
                          strokeWidth={2.5}
                          fill="transparent"
                          dot={false}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
              
              <div className="flex justify-center gap-4 mt-2 text-[10px] text-gray-500 font-medium">
                {loyaltyYearlyKeys.map((key, index) => (
                  <span key={key} className="flex items-center gap-1.5 uppercase">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: YEAR_COLORS[index % YEAR_COLORS.length],
                      }}
                    />
                    {key.replace("v", "")}
                  </span>
                ))}
              </div>
            </div>


        </div>

        {/* ========================================================================= */}
        {/* OFFER REPORT SECTION */}
        {/* ========================================================================= */}
        <div className="space-y-8 pt-6 border-t border-white/5">
          <div className="flex justify-between items-center border-b border-white/5 pb-4">
            <h1 className="text-2xl font-semibold tracking-wide text-white">Offer Report</h1>
            
            <div className="flex items-center gap-3">
              {offerComparisonRange === "monthly" && (
                <>
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] rounded-md px-3 py-1.5 border border-white/10 text-sm appearance-none pr-8 text-white"
                      value={offerComparisonMonth}
                      onChange={(e) => {
                        const m = Number(e.target.value);
                        setOfferComparisonMonth(m);
                        requestOfferComparisonData("monthly", offerComparisonYear, m);
                      }}
                    >
                      {recentMonths.map((month) => (
                        <option key={month.value} value={month.value}>
                          {month.label}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={14}
                      className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] rounded-md px-4 py-1.5 border border-white/10 text-sm appearance-none pr-8 text-white"
                      value={offerComparisonYear}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setOfferComparisonYear(y);
                        requestOfferComparisonData("monthly", y, offerComparisonMonth);
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={14}
                      className="text-gray-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </>
              )}
              <DashboardRangeTabs
                options={[
                  { label: "Weekly", value: "weekly" },
                  { label: "Monthly", value: "monthly" },
                ]}
                value={offerComparisonRange}
                onChange={(value) => {
                  setOfferComparisonRange(value);
                  if (value === "weekly") {
                    requestOfferComparisonData("weekly");
                  } else {
                    requestOfferComparisonData("monthly", offerComparisonYear, offerComparisonMonth);
                  }
                }}
                shape="full"
                className="border-white/10"
                buttonClassName="px-4 md:px-5 py-1.5 text-xs"
              />
            </div>
          </div>

          <div className="grid grid-cols-5 gap-5">
            {offerItems.map((item) => (
              <div
                key={item.key}
                className="bg-[#1A1A1A] border border-white/10 rounded-xl p-6 h-36 flex flex-col justify-between shadow-lg"
              >
                <p className="text-gray-400 text-sm font-medium">{item.label}</p>
                <StatValue
                  value={offerReports?.stats?.[item.key] || 0}
                  color={item.color}
                  loading={isOfferInitialLoading && !offerReports}
                  currency={currency}
                />
              </div>
            ))}
          </div>

          <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8 relative">
            <div className="flex justify-between items-center mb-10">
              <h2 className="text-lg font-medium">
                {offerComparisonRange === "weekly" ? "Weekly" : "Monthly"} Comparison
              </h2>
              <div className="flex gap-6 text-[11px] font-medium tracking-wide">
                {offerItems.map((item) => (
                  <span key={item.key} className="flex items-center gap-2 text-gray-300">
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{ backgroundColor: item.color }}
                    />{" "}
                    {item.label}
                  </span>
                ))}
              </div>
            </div>

            <div className="h-[400px] w-full">
              {isOfferComparisonLoading ? (
                <ChartSkeleton height={400} />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  {offerComparisonRange === "weekly" ? (
                    <BarChart
                      data={offerComparisonData}
                      barCategoryGap="18%"
                      barGap={2}
                    >
                      <CartesianGrid
                        vertical={false}
                        stroke="#6B7280"
                        strokeOpacity={0.45}
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#F3F4F6", fontSize: 12 }}
                        dy={10}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#F3F4F6", fontSize: 12 }}
                        tickFormatter={formatWeeklyAxisTick}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.03)" }}
                        content={<CustomTooltip currency={currency} />}
                      />
                      {offerItems.map((item) => (
                        <Bar
                          key={item.key}
                          dataKey={item.key}
                          fill={item.color}
                          radius={[2, 2, 0, 0]}
                          barSize={10}
                        />
                      ))}
                    </BarChart>
                  ) : (
                    <AreaChart data={offerComparisonData}>
                      <defs>
                        {offerItems.map((item) => (
                          <linearGradient
                            key={item.key}
                            id={`grad-offer-${item.key}`}
                            x1="0"
                            y1="0"
                            x2="0"
                            y2="1"
                          >
                            <stop
                              offset="5%"
                              stopColor={item.color}
                              stopOpacity={0.15}
                            />
                            <stop
                              offset="95%"
                              stopColor={item.color}
                              stopOpacity={0}
                            />
                          </linearGradient>
                        ))}
                      </defs>
                      <CartesianGrid vertical={false} stroke={OFFER_COLORS.grid} />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#666", fontSize: 12 }}
                        dy={15}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#666", fontSize: 12 }}
                      />
                      <Tooltip content={<CustomTooltip currency={currency} />} />
                      {offerItems.map((item) => (
                        <Area
                          key={item.key}
                          type="monotone"
                          dataKey={item.key}
                          stroke={item.color}
                          strokeWidth={2.5}
                          fill={`url(#grad-offer-${item.key})`}
                          dot={false}
                        />
                      ))}
                    </AreaChart>
                  )}
                </ResponsiveContainer>
              )}
            </div>
          </div>


            
            {/* Offer Total Value Chart */}
            <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8 flex flex-col justify-between">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <p className="text-gray-400 text-sm mb-1 font-medium">Total Value</p>
                  {isOfferTotalLoading ? (
                    <Skeleton className="h-10 w-36" />
                  ) : (
                    <p className="text-3xl font-bold text-[#FBBF24]">
                      {formatCurrency(offerTotalValueFromFilteredData, currency)}
                    </p>
                  )}
                </div>
                <div className="flex gap-2 items-center">
                  {offerTotalRange === "monthly" && (
                    <div className="relative">
                      <select
                        className="bg-[#1A1A1A] rounded px-2 py-1 border border-white/10 text-xs appearance-none pr-6 text-white"
                        value={offerTotalMonth}
                        onChange={(e) => {
                          const m = Number(e.target.value);
                          setOfferTotalMonth(m);
                          requestOfferTotalValueData("monthly", offerTotalYear, m);
                        }}
                      >
                        {recentMonths.map((month) => (
                          <option key={month.value} value={month.value}>
                            {month.label}
                          </option>
                        ))}
                      </select>
                      <ChevronDown
                        size={12}
                        className="text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
                      />
                    </div>
                  )}
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] rounded px-3 py-1 border border-white/10 text-xs appearance-none pr-6 text-white"
                      value={offerTotalYear}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setOfferTotalYear(y);
                        if (offerTotalRange === "yearly") {
                          requestOfferTotalValueData("yearly", y);
                        } else {
                          requestOfferTotalValueData("monthly", y, offerTotalMonth);
                        }
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={year} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={12}
                      className="text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                  <DashboardRangeTabs
                    options={[
                      { label: "M", value: "monthly" },
                      { label: "Y", value: "yearly" },
                    ]}
                    value={offerTotalRange}
                    onChange={(value) => {
                      setOfferTotalRange(value);
                      if (value === "yearly") {
                        requestOfferTotalValueData("yearly", offerTotalYear);
                      } else {
                        requestOfferTotalValueData("monthly", offerTotalYear, offerTotalMonth);
                      }
                    }}
                    shape="full"
                    className="border-white/10"
                    buttonClassName="px-2 py-1 text-[10px]"
                  />
                </div>
              </div>

              <div className="h-[400px] w-full">
                {isOfferTotalLoading ? (
                  <ChartSkeleton height={300} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={offerTotalValueData}>
                      <CartesianGrid
                        vertical={false}
                        stroke={OFFER_COLORS.grid}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 11 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#9CA3AF", fontSize: 10 }}
                      />
                      <Tooltip
                        cursor={{ fill: "rgba(255,255,255,0.02)" }}
                        content={<CustomTooltip currency={currency} />}
                      />
                      <Bar
                        dataKey="value"
                        fill={OFFER_COLORS.onlineDiscount}
                        radius={[4, 4, 0, 0]}
                        barSize={16}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            {/* Offer Yearly Comparison Chart */}
            <div className="bg-[#1A1A1A]/40 border border-white/5 rounded-2xl p-8 flex flex-col justify-between">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-sm font-medium text-gray-300">Yearly Comparison</h2>
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] border border-white/10 rounded px-2 py-1 text-xs text-gray-300 appearance-none pr-6"
                      value={offerCompareYear1}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setOfferCompareYear1(y);
                        requestOfferYearlyComparisonData(y, offerCompareYear2);
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={`left-${year}`} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={12}
                      className="text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                  <span className="text-[10px] text-gray-600">Vs</span>
                  <div className="relative">
                    <select
                      className="bg-[#1A1A1A] border border-white/10 rounded px-2 py-1 text-xs text-gray-300 appearance-none pr-6"
                      value={offerCompareYear2}
                      onChange={(e) => {
                        const y = Number(e.target.value);
                        setOfferCompareYear2(y);
                        requestOfferYearlyComparisonData(offerCompareYear1, y);
                      }}
                    >
                      {yearOptions.map((year) => (
                        <option key={`right-${year}`} value={year}>
                          {year}
                        </option>
                      ))}
                    </select>
                    <ChevronDown
                      size={12}
                      className="text-gray-400 absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none"
                    />
                  </div>
                </div>
              </div>

              <div className="h-[400px] w-full">
                {isOfferYearlyLoading ? (
                  <ChartSkeleton height={300} />
                ) : (
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={offerYearlyComparisonData}>
                      <CartesianGrid
                        vertical={true}
                        horizontal={false}
                        stroke={OFFER_COLORS.grid}
                        strokeDasharray="3 3"
                      />
                      <XAxis
                        dataKey="label"
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#666", fontSize: 11 }}
                      />
                      <YAxis
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: "#666", fontSize: 10 }}
                      />
                      <Tooltip content={<CustomTooltip currency={currency} />} />
                      {offerYearlyKeys.map((key, index) => (
                        <Area
                          key={key}
                          type="monotone"
                          dataKey={key}
                          stroke={YEAR_COLORS[index % YEAR_COLORS.length]}
                          strokeWidth={2.5}
                          fill="transparent"
                          dot={false}
                        />
                      ))}
                    </AreaChart>
                  </ResponsiveContainer>
                )}
              </div>
              
              <div className="flex justify-center gap-4 mt-2 text-[10px] text-gray-500 font-medium">
                {offerYearlyKeys.map((key, index) => (
                  <span key={key} className="flex items-center gap-1.5 uppercase">
                    <div
                      className="w-2 h-2 rounded-full"
                      style={{
                        backgroundColor: YEAR_COLORS[index % YEAR_COLORS.length],
                      }}
                    />
                    {key.replace("v", "")}
                  </span>
                ))}
              </div>
            </div>


        </div>

      </div>
    </Layout2>
  );
}
